import { Translate } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { getSocialColor, getSocialIcon } from '@change-corgi/design-system-legacy/components/social';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import type { FacebookShareRecommendations } from '../../hooks/useFetchShareRecommendations';
import { useShareRecommendationsTracking } from '../../hooks/useShareRecommendationsTracking';
import type { Props as MainProps } from '../../index';
import { FeedbackVoter } from '../FeedbackVoter';

type Props = {
	shareRecommendations: FacebookShareRecommendations;
	viewContext: MainProps['viewContext'];
};

export function FacebookGroups({ shareRecommendations, viewContext }: Props): JSX.Element | null {
	const {
		actions: { handleLinkClick },
	} = useShareRecommendationsTracking();

	if (!shareRecommendations.length) {
		return null;
	}

	return (
		<Box
			pt={0}
			pb={[12, 0]}
			sx={{
				position: 'relative',
				borderWidth: ['0 0 1px', '0'],
				borderStyle: ['none none solid', 'none'],
				borderColor: 'neutral-grey100',
				'&:first-child': {
					'&:after': {
						content: '""',
						position: 'absolute',
						backgroundColor: 'neutral-grey100',
						zIndex: 1,
						inlineSize: [0, '1px'],
						blockSize: '100%',
						insetBlockStart: 0,
						insetInlineStart: 'calc(100% + 20px)',
					},
				},
				'&:only-child': {
					'&:after': {
						inlineSize: [0, 0],
					},
				},
			}}
		>
			<Box pb={[16, 4]} sx={{ position: 'relative' }}>
				<Heading
					as="h3"
					size={['h5']}
					sx={{ lineHeight: '28px', display: 'flex' }}
					id="facebook-recommendations-title"
					data-qa="recommendations-title"
				>
					<Icon
						icon={getSocialIcon('facebook')}
						size="xxl"
						color={getSocialColor('facebook')}
						m={0}
						mr={8}
						sx={{ lineHeight: '28px' }}
					/>
					<Translate value="corgi.petition.starter_dashboard.recommendations.facebook.title" />
				</Heading>
			</Box>
			<Box as="ul" role="list" data-testid="fb-groups-list">
				{shareRecommendations.map((group) => (
					<Flex
						as="li"
						role="listitem"
						p={0}
						pt={[0, 12]}
						pb={[12, 0]}
						sx={{ alignItems: 'center', width: '100%', justifyContent: 'space-between' }}
						key={group.name}
					>
						<Link
							data-qa="share-recommendation-facebook-link"
							to={group.url}
							variant="primary"
							target="_blank"
							sx={{ fontSize: '16px', lineHeight: '24px', minWidth: '200px', textWrap: 'wrap' }}
							mr={24}
							onClick={() => handleLinkClick(group.name, 'facebook_group', group.metadata.source, viewContext)}
						>
							{group.name}
						</Link>
						<FeedbackVoter recommendation={group} type="facebook" />
					</Flex>
				))}
			</Box>
		</Box>
	);
}
